import { omit } from '@luminovo/commons';
import * as React from 'react';
import type { JSX } from 'react';

import { Switch, SwitchProps } from '../../Switch/Switch';
import { inputSlotWithRef } from '../FieldController/inputSlotWithRef';

export type FieldSwitchProps = Omit<SwitchProps, 'checked' | 'onChange'> & {
    value: boolean;

    onChange: (value: boolean) => void;

    onAfterChange?: (value: boolean) => void;

    /** Shows the opposite of the value, e.g. "included" for a stored "excluded". */
    inverted?: boolean;

    /** react-hook-form's field ref, from `FieldController`. */
    inputRef?: React.Ref<HTMLInputElement>;
};

function FieldSwitchInner(
    { value, onChange, onAfterChange, inverted, inputRef, slotProps, ...rest }: FieldSwitchProps,
    outerRef: React.ForwardedRef<HTMLButtonElement>,
): JSX.Element {
    const displayedValue = inverted ? !value : value;

    return (
        <Switch
            ref={outerRef}
            checked={displayedValue}
            onChange={(_, checked) => {
                const newValue = inverted ? !checked : checked;
                onChange(newValue);
                onAfterChange?.(newValue);
            }}
            slotProps={{ ...slotProps, input: inputSlotWithRef(slotProps?.input, inputRef) }}
            {...omit(rest, 'error', 'helperText')}
        />
    );
}

export const FieldSwitch = React.forwardRef(FieldSwitchInner) as (
    props: FieldSwitchProps & { ref?: React.ForwardedRef<HTMLButtonElement> },
) => JSX.Element;
